// A projeção das viagens de ônibus para a view que a `BusResultsPage` e a
// seleção curta da conversa (`BusOfferShortlist`) desenham — pura, sem React:
// o kit conhece FATOS (minutos, preço, política) e rótulos já em prosa; quem
// sabe idioma e moeda é o host. Uma fonte para as DUAS superfícies: a bancada do
// web-chat e o card de partidas da conversa (genui-kit).
//
// A entrada é ESTRUTURAL (`BusOfferSource`): o kit não importa o wire do BFF nem
// o do agente, e as duas formas cabem nela sem adaptador.
import { parseLocalDateTime } from '@voll-intelligence/dates';
import type { BusOfferView } from '../models.js';
import type { BusFilterableOffer } from './bus_results_rules.js';

/** Uma ponta da viagem: a rodoviária e o horário local (sem fuso). */
interface BusOfferStopSource {
  stopName: string;
  dateTime: string;
}

/** A baldeação: a parada e, quando o fornecedor informa, a chegada e a partida. */
interface BusOfferConnectionSource {
  stopName: string;
  arrivalDateTime?: string;
  departureDateTime?: string;
}

/** O que a projeção lê de uma viagem. */
export interface BusOfferSource {
  offerId: string;
  departure: BusOfferStopSource;
  arrival: BusOfferStopSource;
  durationMinutes: number;
  carrierName: string;
  carrierCode?: string;
  logoUrl?: string;
  seatClass: string;
  availableSeats: number;
  /** O preço POR PESSOA. */
  price: { amount: number; currency: string };
  connections: number;
  connection?: BusOfferConnectionSource;
  policy?: { status: 'in' | 'out' };
}

/** As Preferências de ônibus que mexem na lista (estrutural: o kit não importa
 *  o domínio). `null` = sem preferência. */
export interface BusOfferPreferences {
  carrier: { code: string; name: string } | null;
  seatClass: string | null;
  departureWindow: 'dawn' | 'morning' | 'afternoon' | 'evening' | null;
  preferDirect: boolean;
}

/** As palavras que a projeção precisa do host — resolvidas UMA vez por perna. */
export interface BusResultsViewCopy {
  /** A cabeça do card a partir do dia da partida: "Ida · Ter, 26 ago". */
  headline(departure: Date): string;
  /** "Direto" ou "Com conexão" — "Com conexão em Taubaté" quando a parada é
   *  conhecida. */
  direct: string;
  withConnection: string;
  withConnectionAt(stop: string): string;
  /** Opcional: a conexão pela CONTAGEM ("1 conexão"), no lugar da parada — a
   *  régua do card da conversa, que nomeia a parada no `Detalhes`. */
  connections?(count: number): string;
  /** As linhas da conexão no `Detalhes`: "Conexão em Taubaté", "Chega às
   *  10:10 e sai às 10:40", "Espera de 30min". */
  connectionStop(stop: string): string;
  connectionTimes(arrival: string, departure: string): string;
  connectionWait(duration: string): string;
  /** "Semi leito · 12 poltronas livres". */
  seatsLeft(seatClass: string, seats: number): string;
  outOfPolicy: string;
  priceLabel(amount: number, currency: string): string;
  /** Os selos das Preferências de ônibus: "Sua viação", "Sua classe". */
  preferredCarrier: string;
  preferredSeatClass: string;
  preferredDeparture: string;
}

/** As cidades das pontas da perna: na ida, origem → destino do pedido; na
 *  volta, o contrário (o bus-api lista a volta sem inverter o pedido). */
export interface BusLegCities {
  departure: string;
  arrival: string;
}

/** O nome como comparação: sem acento, caixa, espaço nem hífen — "Semi leito",
 *  "Semileito" e "SEMI-LEITO" são a mesma classe. */
function comparable(label: string): string {
  return label
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[\s-]+/g, '');
}

/** A viagem é da viação preferida: pelo código OU pelo nome. O código do
 *  catálogo (onde a pessoa escolheu) nem sempre é o da viagem — catálogo e busca
 *  podem vir de provedores diferentes (no develop, a 1001 é `1001` no catálogo e
 *  `2` na viagem) —, então o nome comparável também casa. */
export function matchesPreferredCarrier(
  offer: Pick<BusOfferSource, 'carrierCode' | 'carrierName'>,
  carrier: BusOfferPreferences['carrier'],
): boolean {
  if (!carrier) return false;
  return (
    offer.carrierCode === carrier.code || comparable(offer.carrierName) === comparable(carrier.name)
  );
}

/** Os períodos do dia, pelo minuto da partida no relógio local da rodoviária:
 *  madrugada 0–6h, manhã 6–12h, tarde 12–18h, noite 18–24h. */
const DAY_PERIODS: readonly [NonNullable<BusOfferPreferences['departureWindow']>, number][] = [
  ['dawn', 6 * 60],
  ['morning', 12 * 60],
  ['afternoon', 18 * 60],
  ['evening', 24 * 60],
];

/** A viagem parte no período preferido. */
export function matchesPreferredDeparture(
  departureMinute: number,
  window: BusOfferPreferences['departureWindow'],
): boolean {
  if (window === null) return false;
  return DAY_PERIODS.find(([, end]) => departureMinute < end)?.[0] === window;
}

/** A viagem é da classe preferida, pelo nome comparável. */
export function matchesPreferredSeatClass(
  offer: Pick<BusOfferSource, 'seatClass'>,
  seatClass: BusOfferPreferences['seatClass'],
): boolean {
  return seatClass !== null && comparable(offer.seatClass) === comparable(seatClass);
}

/** Quais preferências do viajante a viagem atende. `direct` só ordena: o card
 *  já diz "Direto", e um selo a mais repetiria. */
type PreferenceMatch = {
  carrier: boolean;
  seatClass: boolean;
  departure: boolean;
  direct: boolean;
};

function preferenceMatch(
  offer: BusOfferSource,
  preferences: BusOfferPreferences | undefined,
): PreferenceMatch {
  return {
    carrier: matchesPreferredCarrier(offer, preferences?.carrier ?? null),
    seatClass: matchesPreferredSeatClass(offer, preferences?.seatClass ?? null),
    departure: matchesPreferredDeparture(
      minuteOfDay(offer.departure.dateTime),
      preferences?.departureWindow ?? null,
    ),
    direct: preferences?.preferDirect === true && offer.connections === 0,
  };
}

/** Quantas preferências a viagem atende — as que atendem mais vêm antes. */
function preferenceRankOf(match: PreferenceMatch): number {
  return [match.carrier, match.seatClass, match.departure, match.direct].filter(Boolean).length;
}

/** O selo PESSOAL de cada preferência atendida — o coração, a escolha declarada. */
function preferenceBadges(
  match: PreferenceMatch,
  copy: BusResultsViewCopy,
): BusOfferView['badges'] {
  const badges: readonly [boolean, string, string][] = [
    [match.carrier, 'preferred-carrier', copy.preferredCarrier],
    [match.seatClass, 'preferred-class', copy.preferredSeatClass],
    [match.departure, 'preferred-departure', copy.preferredDeparture],
  ];
  return badges
    .filter(([matched]) => matched)
    .map(([, id, label]) => ({ id, label, tone: 'unknown' as const, icon: 'favorite' as const }));
}

/** "5h 45min", "5h", "45min" — as unidades são as mesmas nos três idiomas. */
export function busDurationLabel(total: number): string {
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  if (hours && minutes) return `${String(hours)}h ${String(minutes)}min`;
  if (hours) return `${String(hours)}h`;
  return `${String(minutes)}min`;
}

/** Minutos desde a meia-noite no relógio LOCAL da rodoviária (a string do
 *  bus-api não tem fuso). Ilegível cai em 0 — a viagem segue na lista. */
function minuteOfDay(dateTime: string): number {
  const date = parseLocalDateTime(dateTime);
  return date ? date.getHours() * 60 + date.getMinutes() : 0;
}

/** "08:00" lido da própria string, sem passar por `Date`. */
export function busTimeLabel(dateTime: string): string {
  return /T(\d{2}:\d{2})/.exec(dateTime)?.[1] ?? '';
}

/** Leito cama e cama deitam; o resto é poltrona — no chip do card e na
 *  forma da poltrona na planta. */
export function seatClassGlyphOf(seatClass: string): BusOfferView['seatClassGlyph'] {
  return /cama/i.test(seatClass) ? 'bed' : 'seat';
}

function connectionLabelOf(offer: BusOfferSource, copy: BusResultsViewCopy): string {
  if (offer.connections === 0) return copy.direct;
  if (copy.connections) return copy.connections(offer.connections);
  return offer.connection ? copy.withConnectionAt(offer.connection.stopName) : copy.withConnection;
}

/** A espera na conexão em minutos — partida da 2ª perna menos chegada da 1ª.
 *  Hora ilegível ou partida antes da chegada: sem espera, nunca um número falso. */
function connectionWaitMinutes(connection: BusOfferConnectionSource): number | null {
  const arrival = parseLocalDateTime(connection.arrivalDateTime);
  const departure = parseLocalDateTime(connection.departureDateTime);
  if (!arrival || !departure) return null;
  const minutes = Math.round((departure.getTime() - arrival.getTime()) / 60_000);
  return minutes >= 0 ? minutes : null;
}

/** As linhas da conexão no `Detalhes` — só o que o fornecedor informou. */
function connectionDetails(offer: BusOfferSource, copy: BusResultsViewCopy): string[] {
  const connection = offer.connections > 0 ? offer.connection : undefined;
  if (!connection) return [];
  const arrival = busTimeLabel(connection.arrivalDateTime ?? '');
  const departure = busTimeLabel(connection.departureDateTime ?? '');
  const wait = connectionWaitMinutes(connection);
  return [
    copy.connectionStop(connection.stopName),
    ...(arrival && departure && wait !== null ? [copy.connectionTimes(arrival, departure)] : []),
    ...(wait === null ? [] : [copy.connectionWait(busDurationLabel(wait))]),
  ];
}

function toOfferView(
  offer: BusOfferSource,
  cities: BusLegCities,
  travelers: number,
  copy: BusResultsViewCopy,
  match: PreferenceMatch,
): BusOfferView {
  const departure = parseLocalDateTime(offer.departure.dateTime);
  return {
    id: offer.offerId,
    headline: departure ? copy.headline(departure) : '',
    durationLabel: busDurationLabel(offer.durationMinutes),
    connectionLabel: connectionLabelOf(offer, copy),
    connections: offer.connections,
    company: {
      name: offer.carrierName,
      ...(offer.logoUrl ? { logoUrl: offer.logoUrl } : {}),
    },
    departure: {
      timeLabel: busTimeLabel(offer.departure.dateTime),
      cityLabel: cities.departure,
      stationLabel: offer.departure.stopName,
    },
    arrival: {
      timeLabel: busTimeLabel(offer.arrival.dateTime),
      cityLabel: cities.arrival,
      stationLabel: offer.arrival.stopName,
    },
    seatClassLabel: offer.seatClass,
    seatClassGlyph: seatClassGlyphOf(offer.seatClass),
    priceLabel: copy.priceLabel(offer.price.amount, offer.price.currency),
    details: [
      ...connectionDetails(offer, copy),
      copy.seatsLeft(offer.seatClass, offer.availableSeats),
    ],
    // Os selos são os das Preferências de ônibus do viajante.
    badges: preferenceBadges(match, copy),
    ...(offer.policy?.status === 'out' ? { outOfPolicyLabel: copy.outOfPolicy } : {}),
    // Poltronas livres a menos que viajantes: a viagem não leva o grupo.
    selectable: offer.availableSeats >= travelers,
  };
}

/** As viagens de UMA perna como o kit as filtra e ordena. */
export function toBusFilterableOffers(
  offers: readonly BusOfferSource[],
  cities: BusLegCities,
  travelers: number,
  copy: BusResultsViewCopy,
  preferences?: BusOfferPreferences,
): BusFilterableOffer[] {
  return offers.map((offer) => {
    const match = preferenceMatch(offer, preferences);
    return {
      offer: toOfferView(offer, cities, travelers, copy, match),
      withinPolicy: offer.policy?.status !== 'out',
      price: offer.price.amount,
      seatClassId: offer.seatClass,
      companyId: offer.carrierName,
      departureMinute: minuteOfDay(offer.departure.dateTime),
      arrivalMinute: minuteOfDay(offer.arrival.dateTime),
      durationMinutes: offer.durationMinutes,
      preferenceRank: preferenceRankOf(match),
    };
  });
}

/** A moeda da lista: a da primeira viagem (uma rota, uma moeda). */
export function busOffersCurrency(offers: readonly BusOfferSource[]): string {
  return offers[0]?.price.currency ?? 'BRL';
}
